import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { createPropFilter, WebLink } from '@news-mono/web-common'
import {
    breakpoint,
    calcRem,
    colors,
    fonts,
    metrics,
    themedValue,
    ThemeMargins,
} from '../__styling'

const bgImage = require('./assets/scoreboard-bg.jpg')

const aflDesktopImage = require('./assets/afl-desktop.jpg')
const aflTabletImage = require('./assets/afl-tablet.jpg')
const aflMobileImage = require('./assets/afl-mobile.jpg')

const MOBILE_WIDGET_HEIGHT = 260
const PREMATCH_MOBILE_WIDGET_HEIGHT = 230
const TABLET_WIDGET_HEIGHT = 246
const DESKTOP_WIDGET_HEIGHT = 360

const TABLET_WIDGET_HEIGHT_PERTHNOW = 278
export interface GridProps {
    verticalSpacing: keyof ThemeMargins | undefined
    verticalGutters?: [keyof ThemeMargins, keyof ThemeMargins]
}
export const StyledScoreBoardWrapper = styled('div')<GridProps>(
    ({ verticalSpacing, verticalGutters, theme }) => ({
        marginBottom:
            verticalSpacing &&
            calcRem(metrics[theme.kind].margins[verticalSpacing]),
        marginTop:
            verticalSpacing &&
            calcRem(metrics[theme.kind].margins[verticalSpacing]),
        paddingTop:
            verticalGutters &&
            calcRem(metrics.sevennews.margins[verticalGutters[0]]),
        paddingBottom:
            verticalGutters &&
            calcRem(metrics.sevennews.margins[verticalGutters[1]]),
    }),
)

export type ScoreboardType = 'cricket' | 'soccer' | 'afl'

interface StyledScoreboardContainerProps {
    isPrematch?: boolean
    type: ScoreboardType
}
export const StyledScoreboardContainer = styled('div', {
    shouldForwardProp: createPropFilter<StyledScoreboardContainerProps>()([
        'type',
        'isPrematch',
    ]),
})<StyledScoreboardContainerProps>(({ isPrematch, type, theme }) => [
    {
        width: '100%',
        minHeight: isPrematch
            ? calcRem(PREMATCH_MOBILE_WIDGET_HEIGHT)
            : calcRem(MOBILE_WIDGET_HEIGHT),
        position: 'relative',
        padding: calcRem(0, 30),
        backgroundImage: `url('${bgImage}')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: calcRem(15),
        '&::after': {
            zIndex: 1,
            position: 'absolute',
            content: '""',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background:
                'radial-gradient(50.14% 50.14% at 50% 50%, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0) 100%),linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.15))',
        },
        [breakpoint('sm')]: {
            padding: calcRem(20, 15),
            height: themedValue(theme, {
                sevennews: calcRem(TABLET_WIDGET_HEIGHT),
                perthnow: calcRem(TABLET_WIDGET_HEIGHT_PERTHNOW),
                fallback: undefined,
            }),
        },
        [breakpoint('lg')]: {
            paddingBottom: calcRem(15),
            paddingTop: 0,
            height: calcRem(DESKTOP_WIDGET_HEIGHT),
        },
    },

    type === 'afl' && {
        backgroundImage: `url(${aflMobileImage})`,

        [breakpoint('sm')]: {
            backgroundImage: `url('${aflTabletImage}')`,
        },
        [breakpoint('lg')]: {
            backgroundImage: `url('${aflDesktopImage}')`,
        },
    },
    ({ theme }) =>
        theme.kind !== 'sevennews' && [
            {
                '&::after': {
                    zIndex: 1,
                    position: 'absolute',
                    content: '""',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                        'radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.44) 100%), linear-gradient(0deg, rgba(81, 45, 109, 0.26), rgba(81, 45, 109, 0.46))',
                },
            },
        ],
])

export const StyledScoreboardContent = styled('div')(({ theme }) => ({
    zIndex: 2,
    height: '90%',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    color: colors.white,
    fontFamily: themedValue(theme, {
        sevennews: fonts.sevennews.headingAlternative,
        perthnow: fonts.perthnow.sansSerif,
        thewest: fonts.thewest.sansSerif,
    }),
    padding: calcRem(24, 0, 14, 0),
    [breakpoint('sm')]: {
        flexWrap: 'unset',
        paddingTop: themedValue(theme, {
            perthnow: 0,
            fallback: undefined,
        }),
    },
    [breakpoint('lg')]: {
        paddingTop: themedValue(theme, {
            sevennews: calcRem(64),
            perthnow: calcRem(32),
            thewest: calcRem(32),
        }),
    },
    [`${StyledBettingOddWrapper}`]: {
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
        paddingTop: calcRem(10),
        [`${StyledBettingOdd}`]: {
            padding: calcRem(2, 12),
            margin: 0,
        },
        [breakpoint('sm')]: {
            display: 'none',
        },
    },
}))

export const StyledScoreboardContentHomeTeam = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'flex-start',
    width: '50%',
    order: 1,
    [breakpoint('sm')]: {
        width: '25%',
    },
})

export const StyledScoreboardContentMatchData = styled('div')({
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    order: 3,
    width: '100%',
    marginTop: calcRem(25),
    [breakpoint('sm')]: {
        width: '50%',
        display: 'flex',
        order: 2,
    },
})

export const StyledScoreboardContentAwayTeam = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'flex-start',
    width: '50%',
    order: 2,
    [breakpoint('sm')]: {
        order: 3,
        width: '25%',
    },
})

interface StyledTeamNameWrapperProps {
    isReverse?: boolean
    isAfl?: boolean
}

export const StyledTeamNameWrapper = styled('div')<StyledTeamNameWrapperProps>(
    ({ isReverse, isAfl, theme }) => ({
        display: 'flex',
        flexDirection: isReverse ? 'row-reverse' : 'row',
        alignItems: 'center',
        marginBottom: themedValue(theme, {
            sevennews: 0,
            fallback: undefined,
        }),
        columnGap: calcRem(10),
        minHeight: themedValue(theme, {
            sevennews: isAfl ? calcRem(64) : calcRem(50),
            perthnow: calcRem(32),
            thewest: calcRem(32),
        }),
        [breakpoint('sm')]: {
            minHeight: themedValue(theme, {
                perthnow: calcRem(64),
                thewest: calcRem(64),
                fallback: undefined,
            }),
        },
        [breakpoint('md')]: {
            marginBottom: themedValue(theme, {
                sevennews: calcRem(9),
                fallback: undefined,
            }),
        },
    }),
)

export const StyledTeamName = styled('p')(({ theme }) => ({
    textTransform: 'uppercase',
    fontSize: calcRem(11),
    lineHeight: '13px',
    letterSpacing: '0.7px',
    fontWeight: 600,
    margin: 0,

    textAlign: 'center',
    [breakpoint('sm')]: {
        fontSize: themedValue(theme, {
            sevennews: calcRem(18),
            fallback: calcRem(16),
        }),
        lineHeight: themedValue(theme, {
            sevennews: '23px',
            fallback: '19px',
        }),
        letterSpacing: '1.2px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(25),
        lineHeight: '32px',
        letterSpacing: '1.75px',
    },
}))

interface TeamLogoProps {
    type?: ScoreboardType
}
export const StyledTeamLogo = styled('img')<TeamLogoProps>(({ type }) => ({
    width: calcRem(25),
    height: calcRem(25),
    display: 'none',
    [breakpoint('sm')]: {
        display: 'block',
    },
    [breakpoint('lg')]: {
        width: type === 'afl' ? calcRem(40) : calcRem(37),
        height: type === 'afl' ? calcRem(40) : calcRem(37),
    },
}))

export const StyledFirstInningScore = styled('p')({
    fontSize: calcRem(18),
    lineHeight: '40px',
    fontWeight: 700,
    margin: 0,
    marginBottom: calcRem(-10),
    minHeight: calcRem(40),
    [breakpoint('sm')]: {
        fontSize: calcRem(22),
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(25),
    },
})

export interface StyledScoreProps {
    hasFirstInning?: boolean
}

export const StyledScore = styled('p')<StyledScoreProps>(
    ({ hasFirstInning }) => ({
        fontSize: calcRem(33),
        lineHeight: '50px',
        letterSpacing: '-1px',
        fontWeight: 700,
        margin: 0,
        marginTop: hasFirstInning ? calcRem(40) : 0,
        [breakpoint('sm')]: {
            fontSize: calcRem(48),
            lineHeight: '77px',
        },
        [breakpoint('lg')]: {
            fontSize: calcRem(91),
            lineHeight: '116px',
            letterSpacing: '-2.2px',
        },
    }),
)

export const StyledAflScore = styled('p')(({ theme }) => ({
    fontSize: calcRem(40),
    lineHeight: '51px',
    letterSpacing: themedValue(theme, {
        sevennews: '-1px',
        fallback: '-2.2px',
    }),
    fontWeight: 700,
    margin: 0,
    [breakpoint('sm')]: {
        fontSize: themedValue(theme, {
            sevennews: calcRem(60),
            fallback: calcRem(72),
        }),
        lineHeight: themedValue(theme, {
            sevennews: '64px',
            perthnow: '86px',
            thewest: '86px',
        }),
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(107),
        lineHeight: themedValue(theme, {
            sevennews: '96px',
            perthnow: '136px',
            thewest: '136px',
        }),
        letterSpacing: '0',
    },
}))
export const StyledAflGoalsBehinds = styled('p')(({ theme }) => ({
    fontSize: calcRem(14),
    lineHeight: '18px',
    fontWeight: 700,
    margin: 0,
    [breakpoint('sm')]: {
        fontSize: calcRem(17),
        lineHeight: '122%',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(23),
        letterSpacing: '0',
        fontWeight: themedValue(theme, {
            thewest: 500,
            fallback: undefined,
        }),
    },
}))

export const StyledScoreOvers = styled('p')({
    fontWeight: 700,
    fontSize: calcRem(12),
    lineHeight: '14px',
    letterSpacing: '9%',
    textAlign: 'center',
    margin: 0,
    [breakpoint('sm')]: {
        fontSize: calcRem(15),
        lineHeight: '20px',
        letterSpacing: '7px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(23),
        lineHeight: '29px',
        letterSpacing: '10px',
    },
})
export const StyledPenaltiesScore = styled('p')({
    fontWeight: 700,
    fontSize: calcRem(12),
    lineHeight: '14px',
    letterSpacing: '0px',
    textAlign: 'center',
    margin: 0,
    [breakpoint('sm')]: {
        fontSize: calcRem(15),
        lineHeight: '20px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(23),
        lineHeight: '29px',
    },
})

interface StyledMatchDateWrapperProps {
    isAfl?: boolean
}

export const StyledMatchDateWrapper = styled(
    'div',
)<StyledMatchDateWrapperProps>(({ isAfl, theme }) => ({
    margin: 0,
    fontWeight: 300,
    fontSize: themedValue(theme, {
        sevennews: calcRem(10),
        fallback: calcRem(12),
    }),
    lineHeight: '13px',
    letterSpacing: '1px',
    textAlign: 'center',
    textTransform: 'uppercase',
    display: 'flex',
    [breakpoint('sm')]: {
        flexDirection: isAfl ? 'row' : 'column',
        columnGap: isAfl ? calcRem(5) : '0px',
        marginBottom: calcRem(4),
        fontSize: calcRem(12),
        lineHeight: '15px',
        letterSpacing: '1px',
    },
    [breakpoint('lg')]: {
        marginBottom: calcRem(13),
        fontSize: calcRem(18),
        lineHeight: '25px',
        letterSpacing: '3px',
    },
}))

export const StyledMatchDateDay = styled('p')({
    margin: 0,
    marginRight: calcRem(8),
    [breakpoint('sm')]: {
        marginRight: 0,
    },
})
export const StyledMatchDate = styled('p')({
    margin: 0,
})

export const StyledMatchWinner = styled('p')({
    margin: 0,
    fontWeight: 700,
    fontSize: calcRem(15),
    lineHeight: '20px',
    letterSpacing: '2px',
    textAlign: 'center',
    textTransform: 'uppercase',
    [breakpoint('sm')]: {
        fontSize: calcRem(15),
        lineHeight: '20px',
        letterSpacing: '3px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(20),
        lineHeight: '24px',
        letterSpacing: '3px',
    },
})

export interface StyledMatchTournamentProps {
    isSmallText?: boolean
}
export const StyledMatchTournament = styled('p')<StyledMatchTournamentProps>(
    ({ isSmallText, theme }) => ({
        margin: themedValue(theme, {
            sevennews: calcRem(8, 0),
            fallback: calcRem(4, 0),
        }),
        padding: themedValue(theme, {
            sevennews: calcRem(2, 0),
            fallback: calcRem(6, 0),
        }),
        fontWeight: 700,
        fontSize: themedValue(theme, {
            sevennews: isSmallText ? calcRem(12) : calcRem(14),
            fallback: calcRem(18),
        }),
        lineHeight: '18px',
        letterSpacing: isSmallText ? '1px' : '1.5px',
        textTransform: 'uppercase',
        width: themedValue(theme, {
            sevennews: isSmallText ? '100%' : '80%',
            fallback: '60%',
        }),
        textAlign: 'center',
        border: `1px solid ${colors.white}`,
        background: themedValue(theme, {
            perthnow: tokens.perthnow.colors.palette.AFL.darkViolet,
            thewest: tokens.perthnow.colors.palette.AFL.darkViolet,
            fallback: undefined,
        }),

        [breakpoint('sm')]: {
            width: isSmallText ? '95%' : '80%',
            fontSize: isSmallText ? calcRem(13) : calcRem(17),
            lineHeight: '22px',
            letterSpacing: isSmallText ? '1px' : '2px',
        },
        [breakpoint('lg')]: {
            fontSize: isSmallText ? calcRem(19) : calcRem(33),
            lineHeight: '43px',
            letterSpacing: '3px',
        },
        //add space between QTR copy and match time on afl match centre
        span: {
            marginLeft: calcRem(33),
        },
    }),
)

export const StyledStadiumLocationWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    fontSize: calcRem(9),
    lineHeight: '11px',
    letterSpacing: '2.6px',
    [breakpoint('sm')]: {
        fontSize: calcRem(12),
        lineHeight: '14px',
        letterSpacing: '3px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(18),
        lineHeight: '22px',
        letterSpacing: '7px',
    },
})
export const StyledMatchStadium = styled('p')({
    margin: 0,
    textTransform: 'uppercase',
    fontWeight: 300,

    textAlign: 'center',
})
export const StyledMatchCity = styled('p')({
    margin: 0,
    marginLeft: calcRem(8),
    textTransform: 'uppercase',
    fontWeight: 600,
    textAlign: 'center',
    [breakpoint('sm')]: {
        marginLeft: 0,
    },
})
export const StyledButton = styled(WebLink)({
    zIndex: 2,
    background: colors.sevennews.red,
    borderRadius: '2px',
    fontSize: calcRem(15),
    lineHeight: '11px',
    letterSpacing: '0.3px',
    fontWeight: 600,
    color: '#fff',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: calcRem(40),
    width: calcRem(230),

    svg: {
        marginRight: calcRem(15),
        '&:first-of-type': {
            marginRight: calcRem(-8),
            filter: 'drop-shadow(2px 0px 9px rgba(0, 0, 0, 0.2))',
        },
    },
    [breakpoint('sm')]: {
        fontSize: calcRem(14),
        lineHeight: '16px',
        width: calcRem(190),
    },
    [breakpoint('md')]: {
        fontSize: calcRem(17),
        width: calcRem(240),
        lineHeight: '17px',
    },
})

export const StyledScoreboardSkeletonWrapper = styled('div')({
    width: '100%',
    minHeight: calcRem(MOBILE_WIDGET_HEIGHT),
    [breakpoint('sm')]: {
        height: calcRem(TABLET_WIDGET_HEIGHT),
    },
    [breakpoint('lg')]: {
        height: calcRem(DESKTOP_WIDGET_HEIGHT),
    },
    '.skeleton': {
        height: '100%',
    },
})

export const StyledNoScoreDash = styled('p')({
    fontSize: calcRem(40),
    lineHeight: '30px',
    letterSpacing: '-1px',
    fontWeight: 700,
    margin: 0,
    [breakpoint('sm')]: {
        fontSize: calcRem(60),
        lineHeight: '77px',
    },
    [breakpoint('lg')]: {
        fontSize: calcRem(91),
        lineHeight: '116px',
        letterSpacing: '-2.2px',
    },
})

export const StyledHalfTimeScore = styled('div')({
    fontSize: calcRem(14),
    textAlign: 'center',
    marginTop: calcRem(15),
    opacity: 0.8,
    [breakpoint('md')]: {
        fontSize: calcRem(25),
    },
})

export const StyledAflRound = styled('p')({
    margin: 0,
    marginBottom: calcRem(5),
    fontWeight: 700,
    fontSize: calcRem(15),
    lineHeight: '20px',
    letterSpacing: '2px',
    textAlign: 'center',
    textTransform: 'uppercase',
    [breakpoint('sm')]: {
        marginTop: calcRem(4),
    },
    [breakpoint('lg')]: {
        marginTop: calcRem(13),
    },
})

export const SponsorRowWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    zIndex: 2,
    alignItems: 'center',
    width: '100%',
}))

export const StyledBettingOddWrapper = styled('div')(({ theme }) => ({
    display: 'none',
    [breakpoint('sm')]: {
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
    },

    a: {
        textDecoration: themedValue(theme, {
            sevennews: 'none',
            fallback: undefined,
        }),
    },
}))

export const StyledBettingOdd = styled('p')(({ theme }) => ({
    fontFamily: themedValue(theme, {
        thewest: fonts.thewest.sansSerif,
        perthnow: fonts.perthnow.sansSerif,
        sevennews: fonts.sevennews.headingAlternative,
    }),
    fontSize: themedValue(theme, {
        perthnow: calcRem(14),
        thewest: calcRem(14),
        sevennews: calcRem(12),
        fallback: undefined,
    }),
    lineHeight: '16px',
    background: themedValue(theme, {
        perthnow: tokens.perthnow.colors.palette.AFL.darkViolet,
        sevennews: tokens.sevennews.colors.palette.matchCentre.bettingBlue,
        thewest: tokens.perthnow.colors.palette.AFL.darkViolet,
    }),
    color: colors.white,
    padding: themedValue(theme, {
        perthnow: calcRem(8, 24),
        thewest: calcRem(8, 24),
        sevennews: calcRem(8, 22),
    }),
    margin: 0,
}))

export const StyledSpnsorWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 2,
    gap: calcRem(16),
    fontSize: themedValue(theme, {
        perthnow: calcRem(13),
        fallback: undefined,
    }),
    fontFamily: themedValue(theme, {
        thewest: fonts.thewest.sansSerif,
        perthnow: fonts.perthnow.sansSerif,
        sevennews: fonts.sevennews.headingAlternative,
    }),
}))
export const StyledSpnsorCopy = styled('p')(({ theme }) => ({
    color: colors.white,
    fontSize: calcRem(10),
    [breakpoint('sm')]: {
        fontsize: calcRem(13),
    },
}))
export const StyledSpnsorLink = styled(WebLink)(({}) => ({
    display: 'flex',
}))
export const StyledSpnsorImage = styled('img')(({ theme }) => ({
    maxWidth: calcRem(97),
    [breakpoint('sm')]: {
        maxWidth: calcRem(140),
    },
    [breakpoint('md')]: {
        maxWidth: themedValue(theme, {
            thewest: 'unset',
            perthnow: 'unset',
            sevennews: calcRem(133),
        }),
    },
}))
